import React, { useEffect, useMemo, useState } from "react";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
    TrendingUp, Wallet, Users, AlertCircle, CalendarDays, Award, Repeat, Download, FileText,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import {
    rangeFor, summarise, toISO, addDays,
    availableFinancialYears, financialYearBounds, financialYearLabel, currentFinancialYearStart,
    REPORTS,
} from "@/lib/analytics";
import { exportReport } from "@/lib/exports";

const NAVY = "#101A3A";
const GOLD = "#C6A25A";
const GOLD_SOFT = "rgba(198,162,90,0.35)";
const CHART_STROKE = "rgba(255,255,255,0.08)";
const CHART_AXIS = "rgba(255,255,255,0.45)";
const CHART_COLORS = [GOLD, "#8FB4C4", "#B78BE0", "#6BC9A9", "#E0A08B", "#E7C877", "#7AA0D8"];

/* ==========================================================================
 * KPI card
 * ======================================================================= */
function KPI({ label, value, sub, tone = "neutral", icon: Icon, testId }) {
    const tones = {
        gold: "ring-1 ring-gold/40",
        neutral: "",
        alert: "ring-1 ring-red-400/40",
    };
    return (
        <div className={`rounded-2xl glass p-4 min-w-0 ${tones[tone]}`} data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-white/60">
                {Icon && <Icon className={`h-3 w-3 shrink-0 ${tone === "gold" ? "text-gold" : ""}`} />}
                <span className="break-words leading-tight">{label}</span>
            </div>
            <div className={`mt-2 font-serif text-2xl md:text-3xl leading-tight break-words ${tone === "gold" ? "text-gold" : "text-white"}`}>{value}</div>
            {sub && <div className="mt-2 text-xs text-white/60 break-words leading-snug">{sub}</div>}
        </div>
    );
}

/* ==========================================================================
 * Filter chips
 * ======================================================================= */
const PRESETS = [
    { key: "today", label: "Today" },
    { key: "week",  label: "Week" },
    { key: "month", label: "Month" },
    { key: "year",  label: "Year" },
    { key: "custom",label: "Custom" },
];

function FilterBar({ value, onChange, customRange, onCustomChange }) {
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                    <button
                        key={p.key}
                        onClick={() => onChange(p.key)}
                        data-testid={`filter-${p.key}`}
                        className={`h-9 px-3 md:px-4 rounded-full text-xs uppercase tracking-[0.14em] border transition-all ${
                            value === p.key
                                ? "bg-gold text-navy border-gold"
                                : "border-navy/15 text-navy/70 hover:text-navy hover:border-navy/40 bg-white"
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            {value === "custom" && (
                <div className="flex flex-wrap gap-2 items-center rounded-xl glass p-3">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-white/60 ml-1">From</label>
                    <input type="date" value={customRange.start} onChange={(e) => onCustomChange({ ...customRange, start: e.target.value })}
                           data-testid="filter-custom-start"
                           className="bg-white/10 border border-white/20 rounded-md px-2 py-1 text-xs text-white [color-scheme:dark]" />
                    <label className="text-[10px] uppercase tracking-[0.14em] text-white/60">To</label>
                    <input type="date" value={customRange.end} onChange={(e) => onCustomChange({ ...customRange, end: e.target.value })}
                           data-testid="filter-custom-end"
                           className="bg-white/10 border border-white/20 rounded-md px-2 py-1 text-xs text-white [color-scheme:dark]" />
                </div>
            )}
        </div>
    );
}

/* ==========================================================================
 * Chart panels
 * ======================================================================= */
function ChartPanel({ title, subtitle, isEmpty, emptyMessage = "No data for this period", children, testId }) {
    return (
        <div className="rounded-2xl glass p-4 min-w-0" data-testid={testId}>
            <div className="flex items-baseline justify-between gap-2">
                <h4 className="font-serif text-white text-lg leading-tight break-words">{title}</h4>
                {subtitle && <span className="text-[10px] uppercase tracking-[0.16em] text-white/50 shrink-0">{subtitle}</span>}
            </div>
            <div className="mt-3 h-56 relative">
                {isEmpty ? (
                    <div className="absolute inset-0 flex items-center justify-center text-center px-4" data-testid={`${testId}-empty`}>
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/45">{emptyMessage}</p>
                    </div>
                ) : children}
            </div>
        </div>
    );
}

function tooltipStyle() {
    return {
        contentStyle: { background: NAVY, border: `1px solid ${GOLD_SOFT}`, borderRadius: 12, color: "#fff" },
        labelStyle: { color: GOLD, fontSize: 11 },
        itemStyle: { color: "#fff" },
    };
}

function RevenueTrendChart({ data }) {
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid stroke={CHART_STROKE} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: CHART_AXIS, fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: CHART_AXIS, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v}`} width={40} />
                <Tooltip {...tooltipStyle()} formatter={(v) => [formatMoney(v), "Revenue"]} />
                <Line type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: GOLD }} />
            </LineChart>
        </ResponsiveContainer>
    );
}
function AppointmentTrendChart({ data }) {
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid stroke={CHART_STROKE} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: CHART_AXIS, fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: CHART_AXIS, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                <Tooltip {...tooltipStyle()} formatter={(v) => [v, "Appointments"]} />
                <Bar dataKey="appointments" fill={GOLD} radius={[3, 3, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}
function TopTreatmentsChart({ data }) {
    const top = (data || []).slice(0, 6).map((s) => ({ name: s.name, revenue: Number(s.revenue.toFixed(2)) }));
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top} layout="vertical" margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={CHART_STROKE} horizontal={false} />
                <XAxis type="number" tick={{ fill: CHART_AXIS, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fill: CHART_AXIS, fontSize: 10 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip {...tooltipStyle()} formatter={(v) => [formatMoney(v), "Revenue"]} />
                <Bar dataKey="revenue" fill={GOLD} radius={[0, 4, 4, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}
function NewVsReturningChart({ n, r }) {
    const data = [
        { name: "New", value: n },
        { name: "Returning", value: r },
    ];
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="50%" outerRadius="80%" paddingAngle={2}>
                    {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} stroke={NAVY} />)}
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11, color: "#fff" }} />
            </PieChart>
        </ResponsiveContainer>
    );
}

/* ==========================================================================
 * Reports section
 * ======================================================================= */
function ReportCard({ reportKey, report, summary, disabled }) {
    const rows = useMemo(() => (disabled ? [] : report.build(summary)), [disabled, report, summary]);
    const subtitle = `${summary.range.start} to ${summary.range.end}`;
    const doExport = (format) => {
        if (disabled) return;
        exportReport(format, reportKey, subtitle, report, rows);
    };
    return (
        <div className="rounded-2xl glass p-4 min-w-0 flex flex-col" data-testid={`report-${reportKey}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h4 className="font-serif text-white text-lg break-words leading-tight">{report.title}</h4>
                    <p className="text-[11px] text-white/55 mt-1 break-words">{rows.length} rows · {subtitle}</p>
                </div>
                <FileText className="h-5 w-5 text-gold shrink-0" strokeWidth={1.5} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
                {["csv", "xlsx", "pdf"].map((fmt) => (
                    <button key={fmt} onClick={() => doExport(fmt)} disabled={disabled || rows.length === 0}
                            data-testid={`export-${reportKey}-${fmt}`}
                            className="h-9 rounded-full border border-white/25 text-[11px] uppercase tracking-[0.14em] text-white hover:bg-gold hover:text-navy hover:border-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-white disabled:hover:border-white/25 inline-flex items-center justify-center gap-1">
                        <Download className="h-3 w-3" />{fmt}
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ==========================================================================
 * StaffDashboard
 * ======================================================================= */
export function ReportsOverview() {
    const { user } = useAuth();
    const [preset, setPreset] = useState("today");
    const [customRange, setCustomRange] = useState(() => {
        const t = toISO(new Date());
        return { start: t, end: t };
    });
    const [fyStart, setFyStart] = useState(() => currentFinancialYearStart());
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appts, setAppts] = useState([]);

    // Effective range for the analytics section
    const range = useMemo(() => (preset === "custom" ? customRange : rangeFor(preset)), [preset, customRange]);
    // Range for the "Business Overview" is always TODAY
    const today = toISO(new Date());
    // Range for the Reports section is the selected UK financial year
    const fyRange = useMemo(() => financialYearBounds(fyStart), [fyStart]);

    // Fetch a single wide window that covers both the analytics range and the
    // selected FY. We also fetch 90 days prior for accurate new-vs-returning.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            const minStart = [range.start, fyRange.start, today].sort()[0];
            const maxEnd = [range.end, fyRange.end, today].sort().reverse()[0];
            const lookbackStart = toISO(addDays(new Date(`${minStart}T00:00:00`), -90));
            try {
                const r = await staffApi.listAppointments({ start_date: lookbackStart, end_date: maxEnd });
                if (!cancelled) setAppts(r.appointments || []);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [range.start, range.end, fyRange.start, fyRange.end, today]);

    const todaySummary = useMemo(() => summarise(appts, { start: today, end: today }), [appts, today]);
    const rangeSummary = useMemo(() => summarise(appts, range),   [appts, range]);
    const fySummary    = useMemo(() => summarise(appts, fyRange), [appts, fyRange]);

    const firstName = (user?.email || "").split("@")[0].split(".")[0];
    const greeting = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : "Angela";

    return (
        <div className="space-y-8" data-testid="reports-overview">
            {/* ---------------- Header ---------------- */}
            <header className="min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap">
                    <h1 className="font-serif font-light text-3xl md:text-4xl text-navy break-words">Business dashboard</h1>
                    <span className="text-xs text-navy/55">· {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                </div>
                <p className="text-sm text-navy/60 mt-1">Welcome back, {greeting}. Live clinic insights.</p>
            </header>

            {error && <ErrorState error={error} onRetry={() => window.location.reload()} />}

            {/* ---------------- Business Overview (Today) ---------------- */}
            <section className="space-y-3" data-testid="business-overview">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <h2 className="font-serif text-navy text-xl">Business overview</h2>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-navy/50">Today · {today}</span>
                </div>
                {loading ? (
                    <Skeleton className="h-32" />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <KPI label="Today's Revenue" value={formatMoney(todaySummary.totalRevenue)} tone="gold" icon={TrendingUp} testId="kpi-today-revenue" />
                        <KPI label="Today's Appointments" value={todaySummary.appointmentCount} icon={CalendarDays} testId="kpi-today-appts" />
                        <KPI label="Deposits Received" value={formatMoney(todaySummary.depositsReceived)} icon={Wallet} testId="kpi-today-deposits" />
                        <KPI label="Outstanding Payments" value={formatMoney(todaySummary.outstandingPayments)} tone={todaySummary.outstandingPayments > 0 ? "alert" : "neutral"} icon={AlertCircle} testId="kpi-today-outstanding" />
                        <KPI label="New Customers Today" value={todaySummary.newCustomerCount} icon={Users} testId="kpi-today-new-customers" />
                    </div>
                )}
            </section>

            {/* ---------------- Sales Analytics ---------------- */}
            <section className="space-y-4" data-testid="sales-analytics">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <h2 className="font-serif text-navy text-xl">Sales analytics</h2>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-navy/50">{range.start} → {range.end}</span>
                </div>
                <FilterBar value={preset} onChange={setPreset} customRange={customRange} onCustomChange={setCustomRange} />

                {loading ? (
                    <>
                        <Skeleton className="h-24" />
                        <Skeleton className="h-64" />
                    </>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <KPI label="Total Revenue"          value={formatMoney(rangeSummary.totalRevenue)} tone="gold" testId="a-total-revenue" />
                            <KPI label="Avg. Spend / Client"    value={formatMoney(rangeSummary.averageSpend)} testId="a-avg-spend" />
                            <KPI label="Returning Customer %"   value={`${rangeSummary.returningPct.toFixed(1)}%`} icon={Repeat} testId="a-returning-pct" />
                            <KPI label="New Customers"          value={rangeSummary.newCustomerCount} icon={Users} testId="a-new-cust" />
                            <KPI label="Most Popular Treatment" value={rangeSummary.mostPopularTreatment?.name || "—"} sub={rangeSummary.mostPopularTreatment ? `${rangeSummary.mostPopularTreatment.count} bookings` : ""} icon={Award} testId="a-popular" />
                            <KPI label="Highest Revenue Treatment" value={rangeSummary.highestRevenueTreatment?.name || "—"} sub={rangeSummary.highestRevenueTreatment ? formatMoney(rangeSummary.highestRevenueTreatment.revenue) : ""} tone="gold" testId="a-highest-rev" />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <ChartPanel title="Revenue trend" subtitle="Daily" testId="chart-revenue-trend"
                                        isEmpty={!(rangeSummary.trend?.length) || !rangeSummary.trend.some(d => (d.revenue || 0) > 0)}
                                        emptyMessage="No revenue recorded for this period">
                                <RevenueTrendChart data={rangeSummary.trend} />
                            </ChartPanel>
                            <ChartPanel title="Appointment trend" subtitle="Daily" testId="chart-appt-trend"
                                        isEmpty={!(rangeSummary.trend?.length) || !rangeSummary.trend.some(d => (d.appointments || 0) > 0)}
                                        emptyMessage="No appointments recorded for this period">
                                <AppointmentTrendChart data={rangeSummary.trend} />
                            </ChartPanel>
                            <ChartPanel title="Top treatments" subtitle="By revenue" testId="chart-top-treatments"
                                        isEmpty={!(rangeSummary.services?.length)}
                                        emptyMessage="No treatments booked in this period">
                                <TopTreatmentsChart data={rangeSummary.services} />
                            </ChartPanel>
                            <ChartPanel title="New vs returning" subtitle="Customers" testId="chart-new-vs-returning"
                                        isEmpty={(rangeSummary.newCustomerCount || 0) + (rangeSummary.returningCount || 0) === 0}
                                        emptyMessage="No customers recorded for this period">
                                <NewVsReturningChart n={rangeSummary.newCustomerCount} r={rangeSummary.returningCount} />
                            </ChartPanel>
                        </div>
                    </>
                )}
            </section>

            {/* ---------------- Reports ---------------- */}
            <section className="space-y-4" data-testid="reports-section">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <h2 className="font-serif text-navy text-xl">Reports & exports</h2>
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] uppercase tracking-[0.18em] text-navy/60">UK tax year</label>
                        <select
                            value={fyStart}
                            onChange={(e) => setFyStart(Number(e.target.value))}
                            data-testid="fy-selector"
                            className="bg-white border border-navy/20 rounded-md text-xs text-navy px-2 py-1.5 focus:outline-none focus:border-gold"
                        >
                            {availableFinancialYears().map((y) => (
                                <option key={y} value={y}>FY {financialYearLabel(y)}  ({y}-04-06 → {y + 1}-04-05)</option>
                            ))}
                        </select>
                    </div>
                </div>
                <p className="text-xs text-navy/55">Exports use the selected UK financial year (6 April – 5 April). Choose CSV, Excel (.xlsx) or PDF for each report.</p>

                {loading ? (
                    <Skeleton className="h-64" />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(REPORTS).map(([key, report]) => (
                            <ReportCard key={key} reportKey={key} report={report} summary={fySummary} disabled={loading} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

export default ReportsOverview;
